import * as React from 'react';
import { PolarisFormik } from '@amzn/et-polaris-utils';
import * as yup from 'yup';
import { RouteChildrenProps, withRouter } from 'react-router';
import { ReactElement, useReducer, useState } from 'react';
import { useCallback } from 'react';
import { AtmsApiClient } from '@amzn/et-console-components';
import { useEffect } from 'react';
import {
  Button,
  ColumnLayout,
  Flash,
  Form,
  FormField,
  FormSection,
  Select,
} from '@amzn/awsui-components-react/polaris';
import { formatLocaleCode } from '@amzn/et-console-components';
import { css } from 'emotion';
import styled from '@emotion/styled';
import { stringify } from 'query-string';
import I18n from '../../setupI18n';
import { publishCountMetric } from '../metricHelper';

const formSpacing = css`
  padding-top: 14px;
  padding-bottom: 14px;
`;

const FormContainer = styled('div')`
  max-width: 800px;
  min-width: 280px;
`;

interface ReducerState {
  isLoadingTargetLocales: boolean;
  isError: boolean;
  error?: object;
  targetLocales: string[];
  targetLocale?: string;
}

export const SelectTermBaseWorkflow = withRouter(
  ({ location: { pathname }, history }: RouteChildrenProps) => {
    // TODO: We should be able to get this from the Router.match.params prop
    const projectUID = pathname.substr(pathname.lastIndexOf('/') + 1);

    const ANY_TARGET_LOCALE_ID = '_any_';

    const init = (): ReducerState => ({
      isLoadingTargetLocales: false,
      isError: false,
      targetLocales: [],
    });

    const reducer = (state, action): ReducerState => {
      switch (action.type) {
        case 'loadingTargetLocales':
          return {
            ...state,
            isLoadingTargetLocales: true,
            isError: false,
          };
        case 'loadedTargetLocales':
          return {
            ...state,
            isLoadingTargetLocales: false,
            targetLocales: action.targetLocales,
          };
        case 'loadTargetLocalesFailed':
          return {
            ...state,
            isLoadingTargetLocales: false,
            isError: true,
            error: action.error,
          };
        default:
          return state;
      }
    };

    const [reducerState, dispatch] = useReducer(reducer, null, init);
    const [validating, setValidating] = useState(false);
    const { isLoadingTargetLocales, targetLocales } = reducerState;

    const handleLoadTargetLocales = useCallback(() => {
      // Use getProject API
      dispatch({
        type: 'loadingTargetLocales',
      });
      AtmsApiClient.httpGet(`/api/project?uid=${projectUID}`)
        .then(response => {
          dispatch({ type: 'loadedTargetLocales', targetLocales: response.targetLangs });
        })
        .catch(e => {
          publishCountMetric('loadTargetLocalesFailed-SelectTermBaseWorkflow', 'error', e.message);
          dispatch({ type: 'loadTargetLocalesFailed', error: e });
        });
    }, [projectUID]);

    const handleRedirect = (values): void => {
      const query = {
        project: projectUID,
      };

      if (values.targetLocale !== ANY_TARGET_LOCALE_ID) {
        query['targetLocale'] = values.targetLocale;
      }

      history.push(`/web/project/assignTermBase?${stringify(query)}`);
    };

    useEffect(handleLoadTargetLocales, [handleLoadTargetLocales]);

    /**
     * Per AWS Specifications, only start validating after the initial submit.
     * @param e the event
     * @param handleSubmit formik submit event handler function.
     */
    const enableValidation = (e, handleSubmit): void => {
      e.preventDefault();
      setValidating(true);
      handleSubmit();
    };

    const targetLocalesPlusAll = [undefined, ...targetLocales];

    const renderForm = ({
      values,
      errors,
      handleSubmit,
      handleChange,
      handleBlur,
    }): ReactElement => {
      return (
        <FormContainer>
          <form onSubmit={e => enableValidation(e, handleSubmit)}>
            <Form
              header={I18n.t('Select TBs')}
              actions={
                <div>
                  <Button
                    id="select-term-base"
                    text={I18n.t('Continue')}
                    variant="primary"
                    onClick={e => enableValidation(e, handleSubmit)}
                  />
                </div>
              }
              errorText={
                Object.keys(errors).length !== 0
                  ? I18n.t('The form contains errors. Fix them and resubmit.')
                  : ''
              }
            >
              <FormSection>
                <ColumnLayout>
                  <div data-awsui-column-layout-root="true">
                    <FormField label={I18n.t('Language')}>
                      <Select
                        loading={isLoadingTargetLocales}
                        options={targetLocalesPlusAll.map(targetLocale => {
                          return {
                            id: targetLocale ?? ANY_TARGET_LOCALE_ID,
                            label:
                              (targetLocale && formatLocaleCode(targetLocale)) ||
                              I18n.t('Any (show TBs that have at least one locale in the project)'),
                          };
                        })}
                        selectedId={values.targetLocale || ANY_TARGET_LOCALE_ID}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        controlId="targetLocale"
                      />
                    </FormField>
                  </div>
                </ColumnLayout>
              </FormSection>
              {reducerState.isError && (
                <div className={formSpacing}>
                  <Flash
                    type="error"
                    dismissible={true}
                    content={I18n.t('Please try again. If the issue persists, contact support.')}
                    header={I18n.t('Something went wrong.')}
                  />
                </div>
              )}
            </Form>
          </form>
        </FormContainer>
      );
    };

    const initialFormValues = {
      targetLocale: reducerState.targetLocale,
    };

    const schema = yup.object().shape({
      targetLocale: yup.string().nullable(),
    });

    return (
      <PolarisFormik
        initialValues={initialFormValues}
        validationSchema={schema}
        validateOnChange={validating}
        onSubmit={handleRedirect}
        render={renderForm}
      />
    );
  }
);
